// VerificationTab.js
import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Camera, RotateCcw, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const VerificationTab = ({
  senderPreview,
  handleTakePhoto,
  isCameraOpen,
  cancelCamera,
  capturePhoto,
  switchCamera,
  facingMode,
  senderImageError,
  videoRef,
  canvasRef,
  onRemoveImage,
  onSubmit,
  loading,
  updateFormData
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [preview, setPreview] = useState(senderPreview || null);
  const [imageError, setImageError] = useState(senderImageError || "");
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localFacingMode, setLocalFacingMode] = useState(facingMode || "user");
  
  // Ref for the main container to scroll to
  const verificationRef = useRef(null);

  // Smooth scroll effect when coming from alert
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromAlert = searchParams.get('fromAlert');
    const smoothScroll = searchParams.get('smoothScroll');

    if (fromAlert === 'true' && smoothScroll === 'true') {
      // Small delay to ensure the component is rendered
      setTimeout(() => {
        if (verificationRef.current) {
          // Smooth scroll to the verification component
          verificationRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
          
          // Add highlight effect
          verificationRef.current.style.transition = 'all 0.5s ease';
          verificationRef.current.style.boxShadow = '0 0 0 3px rgba(231, 40, 72, 0.3)';
          verificationRef.current.style.borderRadius = '12px';
          
          setTimeout(() => {
            if (verificationRef.current) {
              verificationRef.current.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }
          }, 2000);
        }
        
        // Remove the parameters from URL to prevent re-scrolling on refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }, 400);
    }
  }, [location]);

  // Sync with parent component state
  useEffect(() => {
    setPreview(senderPreview || null);
  }, [senderPreview]);

  useEffect(() => {
    setImageError(senderImageError || "");
  }, [senderImageError]);

  useEffect(() => {
    setLocalFacingMode(facingMode || "user");
  }, [facingMode]);

  // Enhanced camera open function
  const handleEnhancedTakePhoto = async () => {
    setImageError("");
    try {
      // Check if browser supports mediaDevices
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error("Camera access is not supported in your browser");
        return;
      }

      // Request camera permissions
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: localFacingMode,
          aspectRatio: { ideal: 1.7777777778 }
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      // Call the original handleTakePhoto to sync with parent state
      if (handleTakePhoto) {
        handleTakePhoto();
      }
    } catch (error) {
      console.error("Camera access error:", error);
      let errorMessage = "Unable to access camera. ";
      
      if (error.name === 'NotAllowedError') {
        errorMessage += "Please allow camera permissions in your browser settings.";
      } else if (error.name === 'NotFoundError') {
        errorMessage += "No camera found on your device.";
      } else if (error.name === 'NotSupportedError') {
        errorMessage += "Camera access is not supported in your browser.";
      } else if (error.name === 'NotReadableError') {
        errorMessage += "Camera is already in use by another application.";
      } else {
        errorMessage += "Please check your camera permissions and try again.";
      }
      
      toast.error(errorMessage);
      setImageError(errorMessage);
    }
  };

  // Enhanced capture photo function
  const handleEnhancedCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      toast.error("Camera not ready. Please try again.");
      return;
    }

    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    try {
      // Set canvas dimensions to match video's actual dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext("2d");
      
      // For front camera (user), flip horizontally to make it natural
      if (localFacingMode === "user") {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      
      // Draw the image
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Reset transformation for front camera
      if (localFacingMode === "user") {
        context.setTransform(1, 0, 0, 1, 0, 0);
      }
      
      // Convert to high quality JPEG
      const imageData = canvas.toDataURL("image/jpeg", 0.92);

      // Stop camera stream
      const stream = video.srcObject;
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      }

      // Update preview and form data
      setPreview(imageData);
      setImageError("");
      
      // Update form data if updateFormData function is provided
      if (updateFormData) {
        updateFormData({
          target: { name: "sender_image", value: imageData },
        });
      }
      
      // Call original capturePhoto to sync with parent state
      if (capturePhoto) {
        capturePhoto(imageData);
      }
      
    } catch (error) {
      console.error("Error capturing photo:", error);
      toast.error("Failed to capture photo. Please try again.");
    } finally {
      setTimeout(() => {
        setIsCapturing(false);
      }, 500);
    }
  };

  // Enhanced cancel camera function
  const handleEnhancedCancelCamera = () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        videoRef.current.srcObject = null;
      }
    } catch (error) {
      console.error("Error stopping camera:", error);
    }
    
    // Call original cancelCamera to sync with parent state
    if (cancelCamera) {
      cancelCamera();
    }
  };

  // Enhanced switch camera function
  const handleEnhancedSwitchCamera = async () => {
    const newFacingMode = localFacingMode === "user" ? "environment" : "user";
    setLocalFacingMode(newFacingMode);
    
    try {
      // Stop current stream
      if (videoRef.current && videoRef.current.srcObject) {
        const currentStream = videoRef.current.srcObject;
        currentStream.getTracks().forEach(track => track.stop());
      }
      
      // Start new stream with different camera
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: newFacingMode
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      // Call original switchCamera to sync with parent state
      if (switchCamera) {
        switchCamera(newFacingMode);
      }
      
    } catch (error) {
      console.error("Error switching camera:", error);
      toast.error("Unable to switch camera. Your device might not support multiple cameras.");
      // Revert to previous facing mode
      setLocalFacingMode(localFacingMode);
    }
  };

  // Enhanced remove image function
  const handleEnhancedRemoveImage = () => {
    setPreview(null);
    setImageError("");
    
    // Update form data if updateFormData function is provided
    if (updateFormData) {
      updateFormData({
        target: { name: "sender_image", value: "" },
      });
    }
    
    // Call original onRemoveImage to sync with parent state
    if (onRemoveImage) {
      onRemoveImage();
    }
    
    toast.info("Verification photo removed");
  };

  // Enhanced submit with smooth navigation back
  const handleSubmitWithNavigation = async () => {
    if (!preview) {
      setImageError("Verification photo is required");
      toast.error("Please take a verification photo to continue.");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit();
      
      // Show success message
      // toast.success("🎉 Verification completed successfully!");
      
      // Smooth navigation back to home after delay
      setTimeout(() => {
        navigate('/');
      }, 1200);
    } catch (error) {
      toast.error("Verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      ref={verificationRef} 
      className="w-full max-w-4xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-lg transition-all duration-500 ease-in-out"
      style={{
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}
    >
      {/* Alert Indicator */}
      {location.search.includes('fromAlert=true') && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg animate-pulse">
          <div className="flex items-center gap-3">
            <AlertCircle size={20} className="text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-blue-700 font-medium text-sm">
                Verification Required
              </p>
              <p className="text-blue-600 text-xs mt-1">
                Please complete your identity verification to continue using all platform features.
              </p>
            </div>
          </div>
        </div>
      )}

      <h2 className="text-2xl sm:text-3xl font-bold text-center mb-2 text-gray-800">
        Identity Verification
      </h2>
      <p className="text-center text-gray-600 mb-6 sm:mb-8 text-sm sm:text-base">
        Please take a clear photo for identity verification purposes
      </p>

      {/* Verification Status Indicator */}
      <div className="flex items-center justify-center mb-6">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
          preview ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
        }`}>
          {preview ? (
            <CheckCircle size={16} className="text-green-600" />
          ) : (
            <AlertCircle size={16} className="text-yellow-600" />
          )}
          <span className="text-sm font-medium">
            {preview ? 'Photo Ready for Verification' : 'Verification Photo Required'}
          </span>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <AlertCircle size={20} className="text-yellow-400 mt-0.5" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Important Verification Required
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                This verification photo is required to confirm your identity and ensure the security of your account. 
                Your photo will be used for profile verification purposes only.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Camera & Preview Section */}
      <div className="space-y-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2 justify-center">
          <Camera size={20} />
          Take Verification Photo
        </h3>
        
        {!isCameraOpen && (
          <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 bg-gray-50 min-h-80 transition-all duration-300 hover:border-gray-400">
            {preview ? (
              <div className="text-center space-y-6 w-full">
                <div className="relative inline-block">
                  <img
                    src={preview}
                    alt="Verification preview"
                    className="w-48 h-48 sm:w-56 sm:h-56 object-cover rounded-lg shadow-md border-4 border-white mx-auto"
                  />
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1">
                    <CheckCircle size={20} />
                  </div>
                </div>
                <div className="space-y-4">
                  <p className="text-green-600 font-medium text-lg">✓ Photo Captured Successfully!</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      type="button"
                      onClick={handleEnhancedTakePhoto}
                      disabled={isCapturing}
                      className="px-6 py-3 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition-all duration-200 flex items-center gap-2 shadow-lg font-medium hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Camera size={20} /> 
                      {isCapturing ? "Opening Camera..." : "Take New Photo"}
                    </button>
                    <button
                      type="button"
                      onClick={handleEnhancedRemoveImage}
                      className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 shadow-lg font-medium hover:scale-105 active:scale-95"
                    >
                      Remove Photo
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Camera className="text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 mb-2 text-xl font-medium text-center">
                  Ready to Verify Your Identity
                </p>
                <p className="text-gray-500 text-sm mb-6 text-center max-w-md">
                  Take a clear, well-lit photo for profile verification. 
                  {localFacingMode === "environment" ? " Use the back camera for best quality." : " Use the front camera for self-portrait."}
                </p>
                <button
                  type="button"
                  onClick={handleEnhancedTakePhoto}
                  disabled={isCapturing}
                  className="px-8 py-4 bg-[#e72848] text-white rounded-lg hover:bg-[#d62040] transition-all duration-200 flex items-center gap-3 shadow-xl font-medium text-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera size={24} /> 
                  {isCapturing ? "Opening Camera..." : "Take Verification Photo"}
                </button>
              </>
            )}
          </div>
        )}

        {/* Camera Capture Modal */}
        {isCameraOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50 transition-all p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 w-full max-w-2xl relative overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <h3 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-2">
                📸 Take Your Verification Photo
              </h3>
              <p className="text-gray-600 text-center mb-4 text-sm sm:text-base">
                {localFacingMode === "user" 
                  ? "Front Camera - Photo will be mirrored for natural appearance" 
                  : "Back Camera - For highest quality photos"}
              </p>

              {/* Camera Indicator */}
              <div className="flex justify-center items-center gap-2 mb-4">
                <span className="text-sm text-gray-600 font-medium">
                  {localFacingMode === "user" ? "Front Camera" : "Back Camera"}
                </span>
                <button
                  onClick={handleEnhancedSwitchCamera}
                  className="p-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-all duration-200 hover:scale-110"
                  title="Switch Camera"
                  disabled={isCapturing}
                >
                  <RotateCcw size={16} className={isCapturing ? "opacity-50" : ""} />
                </button>
              </div>

              {/* Video Container */}
              <div className="relative rounded-xl overflow-hidden bg-black mx-auto max-w-lg">
                <video
                  ref={videoRef}
                  className={`w-full h-auto max-h-[50vh] object-contain rounded-lg ${
                    localFacingMode === "user" ? "scale-x-[-1]" : ""
                  }`}
                  autoPlay
                  playsInline
                  muted
                ></video>

                {/* Camera guide overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-48 h-60 sm:w-64 sm:h-80 border-2 border-white border-dashed rounded-lg opacity-40"></div>
                </div>

                {/* Quality indicator */}
                <div className="absolute top-3 left-3 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs sm:text-sm">
                  {localFacingMode === "environment" ? "📷 High Quality" : "🤳 Self Portrait"}
                </div>
              </div>

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Control Buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mt-6">
                <button
                  onClick={handleEnhancedCapturePhoto}
                  disabled={isCapturing}
                  className="px-6 sm:px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg shadow-lg hover:scale-105 transition-transform duration-200 font-semibold flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCapturing ? (
                    <>
                      <CircularProgress size={16} color="inherit" />
                      Capturing...
                    </>
                  ) : (
                    <>
                      <Camera size={20} /> 
                      Capture Photo
                    </>
                  )}
                </button>
                <button
                  onClick={handleEnhancedCancelCamera}
                  disabled={isCapturing}
                  className="px-6 sm:px-8 py-3 bg-gray-200 text-gray-800 rounded-lg shadow hover:bg-gray-300 hover:scale-105 transition-transform duration-200 font-semibold disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>

              {/* Camera Tips */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2 font-medium">
                  💡 Tips for best verification results:
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                  <span className="flex items-center justify-center">✅ Good lighting</span>
                  <span className="flex items-center justify-center">✅ Steady hands</span>
                  <span className="flex items-center justify-center">✅ Clear background</span>
                  <span className="flex items-center justify-center">✅ Face the camera</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {imageError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {imageError}
            </p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="text-center space-y-4">
        <button
          type="button"
          onClick={handleSubmitWithNavigation}
          disabled={loading || isSubmitting || !preview}
          className={`px-8 py-4 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-[1.02] active:scale-95 shadow-lg hover:shadow-xl flex items-center gap-3 mx-auto text-lg ${
            loading || isSubmitting || !preview
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading || isSubmitting ? (
            <div className="flex items-center space-x-2">
              <CircularProgress color="inherit" size={24} />
              <span>Verifying Your Identity...</span>
            </div>
          ) : (
            <>
              <CheckCircle size={24} />
              Complete Verification
            </>
          )}
        </button>
        
        {/* {!preview && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-orange-700 text-sm flex items-center justify-center gap-2">
              <AlertCircle size={18} />
              Please take a verification photo to continue
            </p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default VerificationTab;